'use client';

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
} from 'lucide-react';

import {
  Alert,
  AlertDescription,
} from '@/shared/components/ui/alert';

import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';

import type {
  AppDispatch,
  RootState,
} from '@/store/store';

import {
  clearResetPasswordState,
} from '../store/authSlice';

import {
  resetPasswordThunk,
} from '../store/authThunk';

export default function ResetPasswordPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');

  const {
    resetPasswordLoading,
    resetPasswordError,
    resetPasswordSuccess,
  } = useSelector(
    (state: RootState) => state.auth
  );

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] =
    useState('');

  const [showPassword, setShowPassword] =
    useState(false);

  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const [validationError, setValidationError] =
    useState<string | null>(null);

  useEffect(() => {
    dispatch(clearResetPasswordState());

    return () => {
      dispatch(clearResetPasswordState());
    };
  }, [dispatch]);

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    setValidationError(null);

    if (!token) {
      setValidationError(
        'Invalid password reset link.'
      );
      return;
    }

    if (password.length < 8) {
      setValidationError(
        'Password must be at least 8 characters.'
      );
      return;
    }

    if (password !== confirmPassword) {
      setValidationError(
        'Passwords do not match.'
      );
      return;
    }

    try {
      await dispatch(
        resetPasswordThunk({
          token,
          password,
        })
      ).unwrap();
    } catch {
      return;
    }
  };

  const handleBackToLogin = () => {
    dispatch(clearResetPasswordState());
    navigate('/');
  };

  if (resetPasswordSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md rounded-xl border bg-card p-6 shadow-sm sm:p-8">
          <div className="flex justify-center">
            <CheckCircle2 className="h-14 w-14 text-green-600" />
          </div>

          <div className="mt-4 space-y-2 text-center">
            <h1 className="text-2xl font-semibold">
              Password Reset Successful
            </h1>

            <p className="text-sm text-muted-foreground">
              Your password has been updated
              successfully. You can now sign in
              using your new password.
            </p>
          </div>

          <Button
            type="button"
            className="mt-6 w-full"
            onClick={handleBackToLogin}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-xl border bg-card p-6 shadow-sm sm:p-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <KeyRound className="h-7 w-7 text-primary" />
            </div>
          </div>

          <h1 className="mt-4 text-2xl font-semibold">
            Reset Password
          </h1>

          <p className="mt-2 text-sm text-muted-foreground">
            Enter your new password below.
          </p>
        </div>

        {(validationError ||
          resetPasswordError) && (
          <Alert
            variant="destructive"
            className="mt-6"
          >
            <AlertDescription>
              {validationError ||
                resetPasswordError}
            </AlertDescription>
          </Alert>
        )}

        {!token ? (
          <div className="mt-6 space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              This password reset link is
              invalid or incomplete.
            </p>

            <Button
              type="button"
              className="w-full"
              onClick={handleBackToLogin}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Sign In
            </Button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-6 space-y-5"
          >
            <div className="space-y-2">
              <Label htmlFor="new-password">
                New Password
              </Label>

              <div className="relative">
                <Input
                  id="new-password"
                  type={
                    showPassword
                      ? 'text'
                      : 'password'
                  }
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => {
                    setPassword(
                      e.target.value
                    );
                    setValidationError(null);
                  }}
                  disabled={
                    resetPasswordLoading
                  }
                  autoComplete="new-password"
                  className="pr-10"
                  minLength={8}
                  required
                />

                <button
                  type="button"
                  onClick={() =>
                    setShowPassword(
                      (current) => !current
                    )
                  }
                  disabled={
                    resetPasswordLoading
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>

              <p className="text-xs text-muted-foreground">
                Password must be at least 8
                characters.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">
                Confirm Password
              </Label>

              <div className="relative">
                <Input
                  id="confirm-password"
                  type={
                    showConfirmPassword
                      ? 'text'
                      : 'password'
                  }
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(
                      e.target.value
                    );
                    setValidationError(null);
                  }}
                  disabled={
                    resetPasswordLoading
                  }
                  autoComplete="new-password"
                  className="pr-10"
                  minLength={8}
                  required
                />

                <button
                  type="button"
                  onClick={() =>
                    setShowConfirmPassword(
                      (current) => !current
                    )
                  }
                  disabled={
                    resetPasswordLoading
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={
                resetPasswordLoading
              }
            >
              {resetPasswordLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                <>
                  <KeyRound className="mr-2 h-4 w-4" />
                  Reset Password
                </>
              )}
            </Button>

            <button
              type="button"
              onClick={handleBackToLogin}
              disabled={resetPasswordLoading}
              className="flex w-full items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Sign In
            </button>
          </form>
        )}
      </div>
    </div>
  );
}